import { Component, Input, OnInit, TemplateRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ProfileService } from 'app/shared/services/profile.service';

import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';

import { IBillingHistory, ITransactionInfo } from 'app/interfaces/profile.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';

import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { EProfileSectionTitle } from 'app/enums/profile.enums';
import {
  EPurchaseState,
  EPurchaseType,
  ETransactionCurrency,
  ETransactionInfoType
} from '../../../../enums/purchases.enums';

@Component({
  selector: 'stxt-one-time-purchase-card',
  standalone: true,
  imports: [CommonModule, SvgIconComponent],
  templateUrl: './one-time-purchase-card.component.html',
  styleUrl: './one-time-purchase-card.component.scss'
})
export class OneTimePurchaseCardComponent implements OnInit, AfterViewInit {
  @Input() purchase: IBillingHistory;
  @ViewChild('tokensPurchase') tokensPurchase: TemplateRef<unknown>;
  @ViewChild('tokensTopupPurchase') tokensTopupPurchase: TemplateRef<unknown>;
  @ViewChild('voiceCallPurchase') voiceCallPurchase: TemplateRef<unknown>;
  @ViewChild('imagePurchase') imagePurchase: TemplateRef<unknown>;
  @ViewChild('subscriptionPurchase') subscriptionPurchase: TemplateRef<unknown>;
  @ViewChild('generateImagePurchase') generateImagePurchase: TemplateRef<unknown>;
  public rightNavSvg: ISvgConfig = { name: 'arrow_right', fill: ESvgTypes.None };
  public purchaseSvg: ISvgConfig = { name: 'coins-solid', fill: ESvgTypes.None };
  public selectedTransaction: IBillingHistory;
  public purchaseType: EPurchaseType;
  protected readonly EPurchaseState = EPurchaseState;
  protected readonly EPurchaseType = EPurchaseType;
  protected readonly ETransactionCurrency = ETransactionCurrency;
  protected readonly ETransactionInfoType = ETransactionInfoType;

  private readonly staticPurchaseTypeMap: Record<
    EPurchaseType,
    Omit<ITransactionInfo, 'template'>
  > = {
    [EPurchaseType.TOKENS]: {
      icon: 'tokens-package',
      display: 'Token package'
    },
    [EPurchaseType.MEDIA]: {
      icon: 'photo-filled',
      display: 'Photo'
    },
    [EPurchaseType.GENERATE_IMAGE]: {
      icon: 'photo-filled',
      display: 'Generated image'
    },
    [EPurchaseType.SUBSCRIPTION]: {
      icon: 'favorite-filled',
      display: 'Subscription'
    },
    [EPurchaseType.TOKENS_TOPUP]: {
      icon: 'token-topup',
      display: 'Tokens'
    },
    [EPurchaseType.VOICE_CALL]: {
      icon: 'call-simple',
      display: 'Voice call: [X] minutes' /// replace X with real duration number
    }
  };

  private purchaseTypeMap: Record<EPurchaseType, ITransactionInfo>;

  constructor(public profileService: ProfileService) {}

  ngOnInit(): void {
    this.purchaseType = this.purchase.transaction_type;
    this.purchaseSvg.name = this.getTransactionInfo(ETransactionInfoType.ICON) as string;
  }

  ngAfterViewInit(): void {
    this.initializePurchaseTypeMap();
  }

  private initializePurchaseTypeMap(): void {
    this.purchaseTypeMap = {
      [EPurchaseType.TOKENS]: {
        ...this.staticPurchaseTypeMap[EPurchaseType.TOKENS],
        template: this.tokensPurchase
      },
      [EPurchaseType.MEDIA]: {
        ...this.staticPurchaseTypeMap[EPurchaseType.MEDIA],
        template: this.imagePurchase
      },
      [EPurchaseType.GENERATE_IMAGE]: {
        ...this.staticPurchaseTypeMap[EPurchaseType.GENERATE_IMAGE],
        template: this.generateImagePurchase
      },
      [EPurchaseType.SUBSCRIPTION]: {
        ...this.staticPurchaseTypeMap[EPurchaseType.SUBSCRIPTION],
        template: this.subscriptionPurchase
      },
      [EPurchaseType.TOKENS_TOPUP]: {
        ...this.staticPurchaseTypeMap[EPurchaseType.TOKENS_TOPUP],
        template: this.tokensTopupPurchase
      },
      [EPurchaseType.VOICE_CALL]: {
        ...this.staticPurchaseTypeMap[EPurchaseType.VOICE_CALL],
        template: this.voiceCallPurchase
      }
    };
  }

  getPurchaseMeta(): ITransactionInfo {
    if (!this.purchaseTypeMap) {
      const staticInfo = this.staticPurchaseTypeMap[this.purchaseType] || {
        icon: 'tip',
        display: 'Purchase'
      };
      return { ...staticInfo };
    }

    return (
      this.purchaseTypeMap[this.purchaseType] || {
        icon: 'tip',
        display: 'Purchase'
      }
    );
  }

  getTransactionInfo(infoType: ETransactionInfoType): string | TemplateRef<unknown> {
    const info = this.getPurchaseMeta();

    switch (infoType) {
      case ETransactionInfoType.ICON:
        return info.icon;
      case ETransactionInfoType.DISPLAY:
        return info.display;
      case ETransactionInfoType.TEMPLATE:
        return info.template;
    }
  }

  openTransactionInfo(billingItem: IBillingHistory): void {
    this.selectedTransaction = billingItem;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_Transaction_Info);
  }

  public getAbsoluteValue(value: number): number {
    return Math.abs(value);
  }
}
